import type { GenerationJob } from "@dramapie/shared";
import { useMemo, useState } from "react";

import { formatPoints } from "../lib/format";
import {
  GENERATION_KIND_META,
  GENERATION_STATUS_META,
  isActiveJob,
  jobProgress,
} from "../lib/generations";

function shotLabel(shotId: string, shotOrderById: ReadonlyMap<string, number>): string {
  const order = shotOrderById.get(shotId);
  return order === undefined ? shotId : `第 ${order} 镜`;
}

function JobCard({
  job,
  shotOrderById,
  canceling,
  onCancel,
}: {
  job: GenerationJob;
  shotOrderById: ReadonlyMap<string, number>;
  canceling: boolean;
  onCancel?: (jobId: string) => void;
}) {
  const meta = GENERATION_KIND_META[job.kind];
  const status = GENERATION_STATUS_META[job.status];
  const { done, failed, total, percent } = jobProgress(job);
  const canCancel = isActiveJob(job.status) && onCancel !== undefined;

  return (
    <li className="dp-panel" style={{ padding: "8px 10px", background: "var(--panel-2)" }}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="dp-mono" style={{ fontSize: 12.5, color: "var(--ink)" }}>
          {meta.label}
        </span>
        <span
          role="status"
          aria-label={`任务状态 ${status.label}`}
          className={`rounded-full px-1.5 py-0.5 text-[12px] font-medium ${status.className}`}
        >
          {status.label}
        </span>
        <span className="ml-auto dp-tiny-note">总点数 {formatPoints(job.estimatedPoints)} 点</span>
        {canCancel ? (
          <button
            type="button"
            aria-label="取消生成任务"
            onClick={() => onCancel?.(job.id)}
            disabled={canceling}
            className="dp-btn sm danger"
          >
            {canceling ? "取消中…" : "取消"}
          </button>
        ) : null}
      </div>

      <div className="mt-1.5 flex items-center gap-2">
        <div
          role="progressbar"
          aria-label={`${meta.label}任务进度`}
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-1 flex-1 overflow-hidden rounded-full"
          style={{ background: "var(--wash-2)" }}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: `${percent}%`,
              background: job.status === "failed" ? "var(--red)" : "var(--amber)",
            }}
          />
        </div>
        <span className="shrink-0 dp-tiny-note">
          {done}/{total} 项
          {failed > 0 ? ` · ${failed} 项失败` : ""}
        </span>
      </div>

      <p className="dp-tiny-note" style={{ marginTop: 3 }}>
        已结算 {formatPoints(job.settledPoints)} 点 · 已退回 {formatPoints(job.refundedPoints)} 点
      </p>

      {job.items.length > 0 ? (
        <ul aria-label={`${meta.label}任务明细`} className="mt-1.5 space-y-1">
          {job.items.map((item) => {
            const itemStatus = GENERATION_STATUS_META[item.status];
            return (
              <li key={item.id} style={{ fontSize: 12 }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span
                      aria-hidden="true"
                      className="h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{
                        background:
                          item.status === "succeeded"
                            ? "var(--jade)"
                            : item.status === "failed"
                              ? "var(--red)"
                              : "var(--amber)",
                      }}
                    />
                    <span className="truncate" style={{ color: "var(--ink-2)" }}>
                      {shotLabel(item.shotId, shotOrderById)}
                    </span>
                    <span className="shrink-0 dp-tiny-note">{itemStatus.label}</span>
                  </span>
                  <span className="shrink-0 dp-tiny-note">{item.points} 点</span>
                </div>
                {item.error ? (
                  <p className="mt-0.5 pl-3" style={{ fontSize: 12, color: "var(--red)" }}>
                    失败原因：{item.error}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="dp-tiny-note" style={{ marginTop: 3 }}>
          等待服务端分配明细…
        </p>
      )}
    </li>
  );
}

// ---------------------------------------------------------------- 成本模型（C-14 / C-15，真实点数口径）

/**
 * 计费口径与 `cfapp/src/domain/generation.ts` 的 `IMAGE_POINTS` / `CLIP_POINTS` 一致：
 * - 首帧 / 尾帧（生图 `gpt-image-2`）：**14 点/张**；
 * - 视频（`minimax-h3`）：**10 点/条**（与时长 / 分辨率无关）。
 *
 * 原型里的 `5 CU 开机 + 2.4 CU/镜` 是演示值；真实计费没有开机成本，前端不照抄 CU。
 */
export type QueueGenerationKind = "keyframe" | "clip";

export const QUEUE_UNIT_POINTS: Record<QueueGenerationKind, number> = {
  keyframe: 14,
  clip: 10,
};

export const QUEUE_KIND_LABEL: Record<QueueGenerationKind, string> = {
  keyframe: "首帧",
  clip: "视频",
};

export const QUEUE_KIND_HINT: Record<QueueGenerationKind, string> = {
  keyframe: "14 点/张",
  clip: "10 点/条",
};

export type QueueModelId = "h3" | "sd";

export interface QueueModel {
  id: QueueModelId;
  name: string;
  note: string;
}

/**
 * 生成通道。真实计费按产出点数，与通道无关：
 * 两个选项只影响排队方式，不影响单价（所以面板不写「开机成本 5 CU」）。
 */
export const QUEUE_MODELS: Record<QueueModelId, QueueModel> = {
  h3: { id: "h3", name: "Colab H3", note: "Colab GPU 通道 · 按产出点数计费，不收开机费" },
  sd: { id: "sd", name: "SD 系列 API", note: "API 通道 · 按产出点数计费，不收开机费" },
};

/** 开机成本的解释文案（`?` 的 hover 内容，C-14）：真实口径是「没有开机成本」。 */
export const QUEUE_STARTUP_TIP =
  "本系统按产出点数计费：首帧 / 尾帧 14 点/张，视频 10 点/条（与时长无关）。Colab H3 只是 GPU 通道，不收开机费；实际扣费以服务端 estimate 结算为准。";

export interface QueueCost {
  model: QueueModel;
  kind: QueueGenerationKind;
  /** 单镜单价（点）。 */
  unitPoints: number;
  count: number;
  /** 真实计费没有开机成本；保留字段让 UI 能明确写「0 点」。 */
  startup: number;
  total: number;
}

/** 队列成本：按勾选数 × 单镜点数；开机成本恒为 0。 */
export function queueCost(input: {
  kind: QueueGenerationKind;
  count: number;
  model?: QueueModelId;
  /** 覆盖单价（服务端 estimate 口径变化时由主 agent 传）；默认与 cfapp 常量一致。 */
  unitPoints?: Partial<Record<QueueGenerationKind, number>>;
}): QueueCost {
  const model = QUEUE_MODELS[input.model ?? "h3"] ?? QUEUE_MODELS.h3;
  const unitPoints = input.unitPoints?.[input.kind] ?? QUEUE_UNIT_POINTS[input.kind];
  const count = Number.isFinite(input.count) ? Math.max(0, Math.trunc(input.count)) : 0;
  return { model, kind: input.kind, unitPoints, count, startup: 0, total: count * unitPoints };
}

// ---------------------------------------------------------------- props

/** 画布上可选入队的镜头（`GenerationQueue` 只读它需要的字段）。 */
export interface QueueShot {
  id: string;
  order: number;
  /** 镜号（默认取 order）；显示在队列行里。 */
  label?: string;
  action?: string;
  thumbUrl?: string | null;
}

export interface QueueRunPayload {
  model: QueueModelId;
  /** 生成类型：首帧（14 点/张）或视频（10 点/条）。 */
  kind: QueueGenerationKind;
  shotIds: string[];
  cost: QueueCost;
}

export interface GenerationQueueProps {
  jobs: GenerationJob[];
  shotOrderById: ReadonlyMap<string, number>;
  /** 正在取消的 job id（按钮进入禁用态）。 */
  cancelingIds?: ReadonlySet<string>;
  onCancel?: (jobId: string) => void;
  /** 队列快照加载失败时的提示；WS 仍会继续推增量。 */
  error?: string | null;

  // ---- 画布浮动队列（C-13 / C-14 / C-15）；不传 `shots` 时退回旧的内嵌任务列表 ----
  /** 画布上可勾选的镜头。传了才渲染「生成队列」浮动按钮 / 面板。 */
  shots?: readonly QueueShot[];
  /** 受控已选镜头；不传则用组件内部 state。 */
  selectedShotIds?: readonly string[];
  onSelectionChange?: (shotIds: string[]) => void;
  /** 受控生成通道；不传则用组件内部 state。 */
  model?: QueueModelId;
  onModelChange?: (model: QueueModelId) => void;
  /** 受控生成类型（首帧 / 视频）；不传则用组件内部 state。 */
  kind?: QueueGenerationKind;
  onKindChange?: (kind: QueueGenerationKind) => void;
  /** 单价覆盖（点）；默认与 cfapp 的 IMAGE_POINTS / CLIP_POINTS 一致。 */
  unitPoints?: Partial<Record<QueueGenerationKind, number>>;
  /** 浮动面板开关（受控 / 非受控都支持）。 */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 点「批量生成」；成本已算好，主 agent 直接拿去发起生成。 */
  onRunBatch?: (payload: QueueRunPayload) => void;
  /** 面板头部的集数说明（例如「第 11 话 · 30 镜」）。 */
  episodeLabel?: string;
}

function defaultLabel(shot: QueueShot): string {
  return shot.label ?? `第 ${shot.order} 镜`;
}

/**
 * 生成队列（C-13 / C-14 / C-15）。
 *
 * 两种形态：
 * 1. 画布浮动：传 `shots` → 右上角「生成队列」按钮 + 浮动面板（勾选镜头、切模型、看成本）；
 * 2. 内嵌列表：不传 `shots` → 保持故事板工具条下方的任务列表（M3 能力不丢）。
 *
 * 成本口径（原型 `genCostData`）：
 * - Colab H3：开机 5 CU（每批只收一次）+ 每镜 2.4 CU；
 * - SD 系列 API：无开机成本，按次计费，不走 CU。
 */
export function GenerationQueue({
  jobs,
  shotOrderById,
  cancelingIds,
  onCancel,
  error,
  shots,
  selectedShotIds,
  onSelectionChange,
  model,
  onModelChange,
  kind,
  onKindChange,
  unitPoints,
  open,
  onOpenChange,
  onRunBatch,
  episodeLabel,
}: GenerationQueueProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [internalSelection, setInternalSelection] = useState<string[]>([]);
  const [internalModel, setInternalModel] = useState<QueueModelId>("h3");
  const [internalKind, setInternalKind] = useState<QueueGenerationKind>("clip");

  const floating = shots !== undefined;
  const isOpen = open ?? internalOpen;
  const activeModel = model ?? internalModel;
  const activeKind = kind ?? internalKind;

  const selection = useMemo<Set<string>>(() => {
    const source = selectedShotIds ?? internalSelection;
    return new Set(source);
  }, [selectedShotIds, internalSelection]);

  const availableShots = useMemo(() => {
    const list = shots ?? [];
    return [...list].sort((a, b) => a.order - b.order);
  }, [shots]);

  const selectedShots = useMemo(
    () => availableShots.filter((shot) => selection.has(shot.id)),
    [availableShots, selection],
  );
  const cost = queueCost({
    kind: activeKind,
    count: selectedShots.length,
    model: activeModel,
    unitPoints,
  });

  function setOpen(next: boolean) {
    if (open === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  }

  function applySelection(next: string[]) {
    const unique = [...new Set(next)];
    if (selectedShotIds === undefined) setInternalSelection(unique);
    onSelectionChange?.(unique);
  }

  function toggleShot(shotId: string, checked: boolean) {
    const next = checked
      ? [...selection, shotId]
      : [...selection].filter((id) => id !== shotId);
    applySelection(next);
  }

  function changeModel(next: QueueModelId) {
    if (model === undefined) setInternalModel(next);
    onModelChange?.(next);
  }

  function changeKind(next: QueueGenerationKind) {
    if (kind === undefined) setInternalKind(next);
    onKindChange?.(next);
  }

  function runBatch() {
    if (selectedShots.length === 0) return;
    onRunBatch?.({
      model: cost.model.id,
      kind: cost.kind,
      shotIds: selectedShots.map((shot) => shot.id),
      cost,
    });
  }

  const jobsBlock =
    jobs.length > 0 ? (
      <section className="dp-gen-jobs" aria-label="进行中的生成任务">
        <header>
          <h4>进行中任务</h4>
          <span>{jobs.length}</span>
        </header>
        <ul className="dp-gen-jobs-list dp-scroll">
          {jobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              shotOrderById={shotOrderById}
              canceling={cancelingIds?.has(job.id) === true}
              onCancel={onCancel}
            />
          ))}
        </ul>
      </section>
    ) : null;

  const errorBlock = error ? (
    <p role="alert" className="dp-gen-error">
      {error}
    </p>
  ) : null;

  // ---- 形态 2：内嵌列表（故事板工具条下方，保持 M3 能力） ----
  if (!floating) {
    return (
      <section aria-label="生成队列" className="dp-queue">
        <header className="dp-queue-head">
          <h3>生成队列</h3>
          <span>{jobs.length > 0 ? `${jobs.length} 个任务` : "空闲"}</span>
        </header>

        {errorBlock}

        {jobs.length === 0 ? (
          <p className="dp-queue-empty">
            还没有生成任务。选中镜头后点「生成首帧」「生成尾帧」或「生成视频」。
          </p>
        ) : (
          <ul className="dp-queue-list dp-scroll">
            {jobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                shotOrderById={shotOrderById}
                canceling={cancelingIds?.has(job.id) === true}
                onCancel={onCancel}
              />
            ))}
          </ul>
        )}
      </section>
    );
  }

  // ---- 形态 1：画布浮动（C-13） ----
  if (!isOpen) {
    return (
      <button
        type="button"
        className="dp-gen-fab"
        title="打开生成队列"
        onClick={() => setOpen(true)}
      >
        <span aria-hidden="true">✦</span>
        生成队列 <b>{selectedShots.length}</b>
      </button>
    );
  }

  return (
    <div className="dp-gen-panel" role="dialog" aria-label="生成队列">
      <header className="dp-gen-head">
        <div>
          <b>生成队列</b>
          <span>
            已选 {selectedShots.length} / {availableShots.length} · 按点数计费 · 批量出片
            {episodeLabel ? ` · ${episodeLabel}` : ""}
          </span>
        </div>
        <button
          type="button"
          className="dp-gen-close"
          aria-label="关闭生成队列"
          title="关闭"
          onClick={() => setOpen(false)}
        >
          ×
        </button>
      </header>

      <div className="dp-gen-model">
        <div className="dp-gen-model-row">
          <label>
            类型
            <select
              aria-label="生成类型"
              value={activeKind}
              onChange={(event) => changeKind(event.target.value as QueueGenerationKind)}
            >
              {(["keyframe", "clip"] as const).map((item) => (
                <option key={item} value={item}>
                  {QUEUE_KIND_LABEL[item]}（{QUEUE_KIND_HINT[item]}）
                </option>
              ))}
            </select>
          </label>
          <label>
            通道
            <select
              aria-label="生成模型"
              value={activeModel}
              onChange={(event) => changeModel(event.target.value as QueueModelId)}
            >
              {Object.values(QUEUE_MODELS).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <span>{cost.model.note}</span>
      </div>

      <div className="dp-gen-tools">
        <span>勾选镜头 · Shift+点击画布镜头也可加入</span>
        <button
          type="button"
          onClick={() => applySelection(availableShots.map((shot) => shot.id))}
        >
          全选
        </button>
        <button type="button" onClick={() => applySelection([])}>
          清空
        </button>
      </div>

      <ul className="dp-gen-list dp-scroll" aria-label="可入队镜头">
        {availableShots.map((shot) => {
          const checked = selection.has(shot.id);
          return (
            <li key={shot.id}>
              <label className={`dp-gen-row${checked ? " is-checked" : ""}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  aria-label={`加入生成队列 ${defaultLabel(shot)}`}
                  onChange={(event) => toggleShot(shot.id, event.target.checked)}
                />
                {shot.thumbUrl ? (
                  <img src={shot.thumbUrl} alt="" loading="lazy" draggable={false} />
                ) : (
                  <span className="dp-gen-thumb-ph" aria-hidden="true">
                    {(shot.action ?? "").trim().slice(0, 1) || "镜"}
                  </span>
                )}
                <span className="dp-gen-id">{defaultLabel(shot)}</span>
                <span className="dp-gen-act">{shot.action ?? ""}</span>
                <span className="dp-gen-cost">{cost.unitPoints} 点</span>
              </label>
            </li>
          );
        })}
      </ul>

      <div className="dp-gen-summary">
        <div className="dp-gen-line">
          <span>
            开机成本
            <span
              className="dp-cost-help"
              role="img"
              aria-label="开机成本说明"
              tabIndex={0}
              data-tip={QUEUE_STARTUP_TIP}
            >
              ?
            </span>
          </span>
          <b>{cost.startup} 点</b>
        </div>
        {cost.count > 0 ? (
          <>
            <div className="dp-gen-line">
              <span>
                {cost.count} 镜 × {cost.unitPoints} 点
              </span>
              <b>{formatPoints(cost.total)} 点</b>
            </div>
            <div className="dp-gen-total">
              合计 <b>{formatPoints(cost.total)} 点</b>
            </div>
          </>
        ) : (
          <div className="dp-gen-empty">勾选镜头后计算总点数</div>
        )}
        {cost.count === 1 ? (
          <div className="dp-gen-warn" role="note">
            只选 1 镜也会单独排一次队；凑批一次提交更省等待时间。
          </div>
        ) : null}
      </div>

      <button
        type="button"
        className="dp-gen-run"
        disabled={selectedShots.length === 0}
        onClick={runBatch}
      >
        {selectedShots.length > 0 ? `批量生成 ${selectedShots.length} 个` : "先勾选镜头"}
      </button>

      {errorBlock}
      {jobsBlock}

      <div className="dp-gen-foot">点数与 cfapp 计费口径一致（首帧 14 / 视频 10）；实际以服务端 estimate 为准。</div>
    </div>
  );
}
